import React, { useCallback } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTier } from '../../context/TierContext';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// D-025 CBP tail-window: render the chip only inside the final 8 days of the
// 90-day pass. Earlier is too far out for urgency; the long span would leave a
// permanent chip for months.
const CBP_TAIL_WINDOW_MS = 8 * MS_PER_DAY;

// Ceil-days-left: "Ends today" when the expiry is within the next 24h.
// Round-up so a trial started 09:00 Mon that expires 09:00 Thu (10d cohort) reads 10, 9, 8 … 1, "Ends today".
const daysLeft = (expiry) => {
  if (!expiry) return null;
  const diffMs = expiry.getTime() - Date.now();
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / MS_PER_DAY);
};

const resolveCountdown = ({
  subscriptionStatus,
  subscriptionId,
  proExpiresAt,
  trialStartedAt,
  trialCohortDays,
  grandfatherExpiresAt,
}) => {
  if (subscriptionStatus === 'trial_active' && trialStartedAt && trialCohortDays) {
    const expiry = new Date(new Date(trialStartedAt).getTime() + trialCohortDays * MS_PER_DAY);
    return { prefix: 'Trial', expiry, kind: 'trial' };
  }
  if (subscriptionStatus === 'grandfather_active' && grandfatherExpiresAt) {
    return { prefix: "Founder's Rate", expiry: new Date(grandfatherExpiresAt), kind: 'grandfather' };
  }
  // D-025 CBP tail-window (active + no subscription_id + pro_expires_at ≤ 8d).
  if (subscriptionStatus === 'active' && !subscriptionId && proExpiresAt) {
    const expiry = new Date(proExpiresAt);
    const diffMs = expiry.getTime() - Date.now();
    if (diffMs > 0 && diffMs <= CBP_TAIL_WINDOW_MS) {
      return { prefix: 'Pass', expiry, kind: 'cbp' };
    }
  }
  return null;
};

const formatSuffix = (days) => {
  if (days === 0) return 'Ends today';
  if (days === 1) return '1 day left';
  return `${days} days left`;
};

const HeaderCountdownChip = () => {
  const {
    subscriptionStatus,
    subscriptionId,
    proExpiresAt,
    trialStartedAt,
    trialCohortDays,
    grandfatherExpiresAt,
    triggerUpgradeModal,
    loading,
  } = useTier();

  const spec = loading
    ? null
    : resolveCountdown({
        subscriptionStatus,
        subscriptionId,
        proExpiresAt,
        trialStartedAt,
        trialCohortDays,
        grandfatherExpiresAt,
      });

  const handlePress = useCallback(() => {
    if (spec?.kind !== 'cbp') return;
    triggerUpgradeModal?.({
      source: 'cbp_header_chip',
      preselectedPricingOption: 'car_buyers_pass',
    });
  }, [spec?.kind, triggerUpgradeModal]);

  if (!spec) return null;
  const days = daysLeft(spec.expiry);
  if (days === null) return null;

  // Urgency: trial/grandfather flip at ≤3 days; CBP flips at ≤2 days
  // (shorter total window, higher-signal tail).
  const urgentThreshold = spec.kind === 'cbp' ? 2 : 3;
  const urgent = days <= urgentThreshold;
  const tappable = spec.kind === 'cbp';
  const a11y = `${spec.prefix}, ${formatSuffix(days)}`;

  const content = (
    <Text style={[styles.text, urgent && styles.textUrgent]}>
      <Text style={styles.prefix}>{spec.prefix}</Text>
      <Text style={styles.separator}> · </Text>
      <Text>{formatSuffix(days)}</Text>
    </Text>
  );

  if (tappable) {
    return (
      <Pressable
        style={[styles.chip, urgent && styles.chipUrgent]}
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={a11y}
      >
        {content}
      </Pressable>
    );
  }

  return (
    <View
      style={[styles.chip, urgent && styles.chipUrgent]}
      accessibilityRole="text"
      accessibilityLabel={a11y}
    >
      {content}
    </View>
  );
};

const styles = StyleSheet.create({
  // Matches HeaderCTA pill dimensions so the row reads as two equal-height controls.
  chip: {
    backgroundColor: 'rgba(5, 86, 247, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(5, 86, 247, 0.35)',
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 5,
  },
  // Last 2–3 days: warmer palette to cue urgency.
  chipUrgent: {
    backgroundColor: 'rgba(255, 123, 0, 0.1)',
    borderColor: 'rgba(255, 123, 0, 0.4)',
  },
  text: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 12,
    lineHeight: 16,
    color: '#6FA0FF',
    letterSpacing: 0.2,
  },
  textUrgent: {
    color: '#FFB073',
  },
  prefix: {
    fontFamily: 'Satoshi-Bold',
  },
  separator: {
    opacity: 0.6,
  },
});

export default HeaderCountdownChip;
